import React, { FC } from 'react';
import Subheading from '../font/Subheading';
import styled from 'styled-components';

import ColumnItem from '../layout/ColumnItem';
import Row from '../layout/Row';
import Spacer from '../layout/Spacer';
import Pill from '../navigation/Pill';
import { useAppDispatch, useAppSelector } from '../../redux/redux.hooks';
import {
  updateAnswerTranslation,
  updateTranslatedQuestion,
  updateTranslations,
} from '../../reducers/translations.reducer';

type TranslationFormProps = {
  shopId: string;
  onLanguageChange: (locale: string) => void;
};

const TranslationForm: FC<TranslationFormProps> = ({
  shopId,
  onLanguageChange,
}) => {
  const dispatch = useAppDispatch();
  const {
    supportedLanguages,
    currentLanguage,
    currentTranslation,
    updateStatus,
    customTranslations,
  } = useAppSelector((state) => state.translationsReducer);
  const { questionLabel, translatedQuestion, answers, translationType } =
    currentTranslation!;

  const languageListItems = () =>
    supportedLanguages.map((locale) => {
      return (
        <Pill
          onClick={() => {
            onLanguageChange(locale);
          }}
          backgroundColor={
            currentLanguage.includes(locale.toLowerCase())
              ? '#35b8583d'
              : '#fafafa66'
          }
        >
          <Subheading>{locale}</Subheading>
        </Pill>
      );
    });

  const onQuestionTextChanged = (value: string) => {
    dispatch(updateTranslatedQuestion(value));
  };

  const onAnswerChanged = (value: string, key: string) => {
    dispatch(updateAnswerTranslation({ key, value }));
  };

  const onSubmit = () => {
    dispatch(
      updateTranslations({
        shopId,
        translations: customTranslations,
        translationType,
      })
    );
  };

  return (
    <>
      <Row>
        <LanguagesContainer>{languageListItems()}</LanguagesContainer>
      </Row>
      <Divider />
      <Spacer width={24} />
      <Row>
        <ColumnItem>
          <s-text-field
            id={translatedQuestion.key}
            name={translatedQuestion.key}
            label={questionLabel}
            value={translatedQuestion.value}
            onInput={(event) => onQuestionTextChanged(event.currentTarget.value)}
            autocomplete='off'
          />
        </ColumnItem>
      </Row>
      {answers.length > 0 && (
        <>
          <Row>
            <ColumnItem>
              <s-heading fontSize='large'>ANSWERS</s-heading>
            </ColumnItem>
          </Row>
          {answers.map((ans) => (
            <Row key={ans.key}>
              <ColumnItem>
                <s-text-field
                  id={ans.key}
                  name={ans.key}
                  label={ans.label}
                  value={ans.value}
                  onInput={(event) => onAnswerChanged(event.currentTarget.value, ans.key)}
                  autocomplete='off'
                />
              </ColumnItem>
            </Row>
          ))}
        </>
      )}
      <FullWidthContainer>
        <s-button variant='primary' onClick={onSubmit} loading={updateStatus === 'loading'}>
          Update
        </s-button>
      </FullWidthContainer>
    </>
  );
};

export default TranslationForm;

const FullWidthContainer = styled.div`
  display: flex;
  margin-top: 20px;
  width: 100%;
  height: auto;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  padding: 0 16px 12px 16px;
`;

const LanguagesContainer = styled.div`
  display: flex;
  width: 80%;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  padding: 0 16px 12px 16px;
`;

const Divider = styled.hr`
  border-width: 0.5px;
  border-color: #ffffff;
`;
